import * as React from 'react';
import { MachineContext } from "../fsm/useMachine"
import { SET_CORNER_RADIUS } from "../fsm/types"
import ImageFlowObject from "../../model/ImageFlowObject"
import PdfFlowObject from "../../model/PdfFlowObject"
import VideoFlowObject from "../../model/VideoFlowObject"
import ShapeFlowObject from "../../model/ShapeFlowObject"
import { getShapeDefinition } from "../../model/ShapeDefinitions";

type CornerTarget = ImageFlowObject | VideoFlowObject | PdfFlowObject | ShapeFlowObject;

export const CornerRadiusPicker: React.FC<{ close: () => void }> = ({ close }) => {
    const [current, send] = React.useContext(MachineContext)
    const currentObject = current.context.currentObject! as CornerTarget
    const isShape = currentObject.type === "shape";

    const maxRadius = Math.max(currentObject.frame.width, currentObject.frame.height) / 2;
    const [radius, setRadius] = React.useState(currentObject.cornerRadius);

    const imgSrc = React.useMemo(() => {
        if (isShape) return undefined;
        const av = currentObject as (ImageFlowObject | VideoFlowObject | PdfFlowObject);
        if (av.croppedImageAsset?.externalURL.__tag === "string") {
            return av.croppedImageAsset!.externalURL.value
        } else if (av.originalImageAsset?.externalURL.__tag === "string") {
            return av.originalImageAsset!.externalURL.value
        }
    }, [current.context.currentObject])

    const applyRadius = (val: number) => {
        setRadius(val);
        send({ type: "DELEGATE", payload: { type: SET_CORNER_RADIUS, radius: val }, id: currentObject.id });
    }

    const previewRadius = Math.min(radius, maxRadius);
    const previewPct = maxRadius > 0 ? `${(previewRadius / maxRadius) * 50}%` : "0";

    const preview = () => {
        if (isShape) {
            const shape = currentObject as ShapeFlowObject;
            const sz = 48;
            const pad = 4;
            const inner = sz - pad * 2;
            const fillColor = shape.color.asRgba();
            const bw = shape.borderWidth > 0 ? shape.borderWidth : 0;
            const strokeColor = bw > 0 ? shape.borderColor.asRgba() : "none";
            const def = getShapeDefinition(shape.shapeType);
            const cr = Math.min(radius, inner / 2);

            let svgChild: React.ReactNode;
            if (shape.shapeType === "ellipse") {
                svgChild = <ellipse cx={sz / 2} cy={sz / 2} rx={inner / 2} ry={inner / 2} />;
            } else if (shape.shapeType === "rectangle" || shape.shapeType === "roundedRectangle") {
                svgChild = <rect x={pad} y={pad} width={inner} height={inner} rx={cr} ry={cr} />;
            } else if (def?.renderType === "path" && def.pathString) {
                const scaled = def.pathString.replace(
                    /(\d+\.?\d*)\s+(\d+\.?\d*)/g,
                    (_m, x, y) => `${parseFloat(x) * inner + pad} ${parseFloat(y) * inner + pad}`
                );
                svgChild = <path d={scaled} fillRule="evenodd" />;
            } else if (shape.pointsArrayString) {
                const points = shape.pointsArrayString
                    .split(" ")
                    .map(pair => {
                        const [x, y] = pair.split(",");
                        return `${parseFloat(x) * inner + pad},${parseFloat(y) * inner + pad}`;
                    })
                    .join(" ");
                svgChild = <polygon points={points} />;
            } else {
                svgChild = <rect x={pad} y={pad} width={inner} height={inner} rx={cr} ry={cr} />;
            }

            return (
                <svg width={sz} height={sz} viewBox={`0 0 ${sz} ${sz}`}>
                    <g
                        style={{
                            fill: fillColor,
                            stroke: strokeColor,
                            strokeWidth: bw,
                        }}
                    >
                        {svgChild}
                    </g>
                </svg>
            );
        }
        return <img src={imgSrc} style={{ borderRadius: previewPct, width: 48, height: 48, objectFit: "cover" }} />;
    }

    const presets: { label: string; value: number }[] = [
        { label: "None", value: 0 },
        { label: "S", value: Math.round(maxRadius * 0.1) },
        { label: "M", value: Math.round(maxRadius * 0.25) },
        { label: "Max", value: Math.round(maxRadius) },
    ];

    return (
        <div id="cornerRadiusPicker" style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 12, alignItems: "center", minWidth: 220 }}>
            {preview()}
            <input
                type="range"
                min={0}
                max={Math.round(maxRadius)}
                step={1}
                value={Math.round(radius)}
                onChange={(e) => applyRadius(parseInt(e.target.value, 10))}
                style={{ width: "100%", accentColor: "var(--foreground)" }}
            />
            <div style={{ display: "flex", gap: 8, width: "100%" }}>
                {presets.map(p => (
                    <button
                        key={p.label}
                        className={Math.round(radius) === p.value ? "active" : ""}
                        onClick={() => applyRadius(p.value)}
                        style={{
                            flex: 1,
                            padding: "4px 0",
                            borderRadius: 6,
                            border: Math.round(radius) === p.value ? "2px solid var(--foreground)" : "1px solid rgba(128,128,128,0.3)",
                            background: Math.round(radius) === p.value ? "var(--foreground)" : "transparent",
                            color: Math.round(radius) === p.value ? "var(--background)" : "var(--foreground)",
                            cursor: "pointer",
                            fontSize: 12,
                            fontWeight: 600,
                        }}
                    >
                        {p.label}
                    </button>
                ))}
            </div>
        </div>
    )
}
